"use client";

import { c as _c } from "react/compiler-runtime";
import { Zap, Eye, Wrench, MessageSquare, Braces, FileJson2, ImagePlus, Gift, AlertCircle } from "lucide-react";
import { useRouter } from "next/navigation";
import { useMemo, useState } from "react";
import { ModelCard } from "@/components/models/model-card";
import { Button } from "@/lib/components/button";
import { isMappingDeactivated } from "@llmgateway/shared/components";
import type { ApiModel, ApiModelProviderMapping, ApiProvider } from "@/lib/fetch-models";
import type { StabilityLevel } from "@llmgateway/models";
import type { LucideProps } from "lucide-react";
interface ModelWithProviders extends ApiModel {
  providerDetails: Array<{
    provider: ApiModelProviderMapping;
    providerInfo: ApiProvider;
  }>;
}
interface ProviderModelsGridProps {
  models: ModelWithProviders[];
}
export function ProviderModelsGrid(t0) {
  const $ = _c(17);
  const {
    models
  } = t0;
  const router = useRouter();
  const [showDeactivated, setShowDeactivated] = useState(false);
  let t1;
  if ($[0] !== models) {
    t1 = models.filter(_temp2);
    $[0] = models;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const deactivatedCount = t1.length;
  let t2;
  bb0: {
    if (showDeactivated) {
      t2 = models;
      break bb0;
    }
    let t3;
    if ($[2] !== models) {
      t3 = models.filter(_temp4);
      $[2] = models;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    t2 = t3;
  }
  const visibleModels = t2;
  const getCapabilityIcons = _temp5;
  const shouldShowStabilityWarning = _temp6;
  const formatPrice = _temp7;
  let t3;
  if ($[4] !== deactivatedCount || $[5] !== showDeactivated) {
    t3 = deactivatedCount > 0 && <div className="mb-6 flex justify-end"><Button variant="outline" size="sm" onClick={() => setShowDeactivated(_temp8)}><AlertCircle className="h-3.5 w-3.5 mr-1.5 text-red-500" />{showDeactivated ? "Hide deactivated models" : `Show ${deactivatedCount} deactivated model${deactivatedCount === 1 ? "" : "s"}`}</Button></div>;
    $[4] = deactivatedCount;
    $[5] = showDeactivated;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== router || $[8] !== visibleModels) {
    let t5;
    if ($[10] !== router) {
      t5 = (model_2, index) => <ModelCard key={`${model_2.providerDetails[0].provider.providerId}-${model_2.id}-${index}`} model={model_2} shouldShowStabilityWarning={shouldShowStabilityWarning} getCapabilityIcons={getCapabilityIcons} goToModel={() => router.push(`/models/${encodeURIComponent(model_2.id)}`)} formatPrice={formatPrice} />;
      $[10] = router;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    t4 = visibleModels.map(t5);
    $[7] = router;
    $[8] = visibleModels;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[12] !== t4) {
    t5 = <div className="grid gap-6 md:grid-cols-3">{t4}</div>;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t3 || $[15] !== t5) {
    t6 = <>{t3}{t5}</>;
    $[14] = t3;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}
function _temp8(prev) {
  return !prev;
}
function _temp7(price, discount) {
  if (price === null || price === undefined) {
    return "\u2014";
  }
  const priceNum = parseFloat(price);
  const discountNum = discount ? parseFloat(discount) : 0;
  const originalPrice = parseFloat((priceNum * 1000000).toFixed(4));
  if (discountNum > 0) {
    const discountedPrice = parseFloat((priceNum * 1000000 * (1 - discountNum)).toFixed(4));
    return <div className="flex flex-col justify-items-center"><div className="flex items-center gap-1"><span className="line-through text-muted-foreground text-xs">${originalPrice}</span><span className="text-green-600 font-semibold">${discountedPrice}</span></div></div>;
  }
  return `$${originalPrice}`;
}
function _temp6(stability) {
  return stability !== null && stability !== undefined && ["unstable", "experimental"].includes(stability);
}
function _temp5(providerMapping, model_1) {
  const capabilities = [];
  if (providerMapping.streaming) {
    capabilities.push({
      icon: Zap,
      label: "Streaming",
      color: "text-blue-500"
    });
  }
  if (providerMapping.vision) {
    capabilities.push({
      icon: Eye,
      label: "Vision",
      color: "text-green-500"
    });
  }
  if (providerMapping.tools) {
    capabilities.push({
      icon: Wrench,
      label: "Tools",
      color: "text-purple-500"
    });
  }
  if (providerMapping.reasoning) {
    capabilities.push({
      icon: MessageSquare,
      label: "Reasoning",
      color: "text-orange-500"
    });
  }
  if (providerMapping.jsonOutput) {
    capabilities.push({
      icon: Braces,
      label: "JSON Output",
      color: "text-cyan-500"
    });
  }
  if (providerMapping.jsonOutputSchema) {
    capabilities.push({
      icon: FileJson2,
      label: "JSON Schema",
      color: "text-teal-500"
    });
  }
  const hasImageGen = Array.isArray(model_1?.output) ? model_1.output.includes("image") : false;
  if (hasImageGen) {
    capabilities.push({
      icon: ImagePlus,
      label: "Image Generation",
      color: "text-pink-500"
    });
  }
  if (providerMapping.discount && parseFloat(providerMapping.discount) > 0) {
    capabilities.push({
      icon: Gift,
      label: `${(parseFloat(providerMapping.discount) * 100).toFixed(0)}% Discount`,
      color: "text-green-500"
    });
  }
  return capabilities;
}
function _temp4(model_0) {
  return model_0.providerDetails.some(_temp3);
}
function _temp3(t0) {
  const {
    provider: provider_0
  } = t0;
  return !isMappingDeactivated(provider_0);
}
function _temp2(model) {
  return model.providerDetails.every(_temp);
}
function _temp(t0) {
  const {
    provider
  } = t0;
  return isMappingDeactivated(provider);
}